/**
 * Dependency-free Sentry client for the browser (and Electron renderer).
 *
 * We POST directly to Sentry's envelope ingest endpoint rather than pull in
 * @sentry/react — the SDK can't be added in this workspace (pnpm install is
 * broken here) and this keeps the bundle tiny. It mirrors the raw-HTTP approach
 * the server's PostHog telemetry already uses.
 *
 * INERT until `VITE_SENTRY_DSN` is set: with no DSN, every capture() is a no-op,
 * so this ships safely and activates the moment the key lands (Pages env var).
 *
 * Swapping to the full @sentry/react SDK later (for auto-instrumentation +
 * source-mapped stacks) only means replacing this module's init/capture calls.
 */

interface ParsedDsn {
  readonly host: string;
  readonly projectId: string;
  readonly publicKey: string;
}

/** Parse `https://<publicKey>@<host>/<projectId>` into its ingest parts. */
function parseDsn(dsn: string): ParsedDsn | null {
  try {
    const u = new URL(dsn);
    const projectId = u.pathname.replace(/^\//, "");
    if (!u.username || !u.host || !projectId) return null;
    return { host: u.host, projectId, publicKey: u.username };
  } catch {
    return null;
  }
}

let dsn: ParsedDsn | null = null;
let environment = "production";
let release: string | undefined;
let currentUser: { id: string } | null = null;
const SURFACE = "web";
// Fraction of performance transactions to send (0..1). Errors are always sent; only
// transactions are sampled. Defaults to 1 when a DSN is set (the user asked for timing),
// override via VITE_SENTRY_TRACES_SAMPLE_RATE. 0 ⇒ tracing off (errors still on).
let tracesSampleRate = 1;

export function initSentry(opts: {
  readonly dsn?: string | undefined;
  readonly environment?: string | undefined;
  readonly release?: string | undefined;
  readonly tracesSampleRate?: number | undefined;
}): void {
  dsn = opts.dsn ? parseDsn(opts.dsn) : null;
  if (opts.environment) environment = opts.environment;
  release = opts.release;
  if (typeof opts.tracesSampleRate === "number" && Number.isFinite(opts.tracesSampleRate)) {
    tracesSampleRate = Math.min(1, Math.max(0, opts.tracesSampleRate));
  }
}

/** Attach the signed-in user to subsequent events (call again with null on sign-out). */
export function setSentryUser(user: { id: string } | null): void {
  currentUser = user && user.id ? { id: user.id } : null;
}

/** True when a DSN is configured — lets callers skip building context when inert. */
export function sentryEnabled(): boolean {
  return dsn !== null;
}

function randomHex(bytes: number): string {
  const arr = new Uint8Array(bytes);
  crypto.getRandomValues(arr);
  return Array.from(arr, (b) => b.toString(16).padStart(2, "0")).join("");
}

/** Minimal stack parse → Sentry frames (best-effort; unparsed lines are dropped). */
function stackFrames(stack: string | undefined): ReadonlyArray<Record<string, unknown>> {
  if (!stack) return [];
  const frames: Array<Record<string, unknown>> = [];
  for (const line of stack.split("\n")) {
    const m = /at\s+(.*?)\s+\(?(.*?):(\d+):(\d+)\)?$/.exec(line.trim());
    if (!m) continue;
    frames.push({
      function: m[1],
      filename: m[2],
      lineno: Number(m[3]),
      colno: Number(m[4]),
      in_app: !m[2]!.includes("node_modules"),
    });
  }
  // Sentry expects frames oldest-first; stacks are newest-first.
  return frames.reverse();
}

function toError(err: unknown): Error {
  if (err instanceof Error) return err;
  if (typeof err === "string") return new Error(err);
  try {
    return new Error(JSON.stringify(err));
  } catch {
    return new Error(String(err));
  }
}

/**
 * Report an error to Sentry. No-op when no DSN is configured. Never throws — a
 * telemetry failure must never break the app, so the POST is fire-and-forget.
 */
export function captureException(err: unknown, extra?: Readonly<Record<string, unknown>>): void {
  if (!dsn) return;
  const e = toError(err);
  const eventId = randomHex(16);
  const nowSec = Date.now() / 1000;
  const envelopeHeader = JSON.stringify({ event_id: eventId, sent_at: new Date().toISOString() });
  const itemHeader = JSON.stringify({ type: "event" });
  const payload = JSON.stringify({
    event_id: eventId,
    timestamp: nowSec,
    platform: "javascript",
    level: "error",
    environment,
    ...(release ? { release } : {}),
    tags: { surface: SURFACE },
    ...(currentUser ? { user: currentUser } : {}),
    ...(extra ? { extra } : {}),
    ...(typeof location !== "undefined" ? { request: { url: location.href } } : {}),
    exception: {
      values: [{ type: e.name, value: e.message, stacktrace: { frames: stackFrames(e.stack) } }],
    },
  });
  const url = `https://${dsn.host}/api/${dsn.projectId}/envelope/?sentry_key=${dsn.publicKey}&sentry_version=7`;
  try {
    void fetch(url, {
      method: "POST",
      // keepalive lets the report survive a page unload (e.g. an error on navigation).
      keepalive: true,
      headers: { "content-type": "application/x-sentry-envelope" },
      body: `${envelopeHeader}\n${itemHeader}\n${payload}\n`,
    }).catch(() => {});
  } catch {
    // ignore — telemetry must never throw into the app
  }
}

/** One child span within a transaction. Timestamps are epoch milliseconds. */
export interface SpanInput {
  readonly op: string;
  readonly description?: string;
  readonly startMs: number;
  readonly endMs: number;
}

/**
 * Send a PERFORMANCE transaction to Sentry (a timed operation, optionally with child
 * spans). No-op without a DSN, and sampled by `tracesSampleRate` (errors are never
 * sampled — only transactions). Fire-and-forget, so it adds no latency to the timed
 * work. Timestamps are epoch ms (e.g. `performance.timeOrigin + performance.now()`).
 */
export function captureTransaction(input: {
  readonly name: string;
  readonly op: string;
  readonly startMs: number;
  readonly endMs: number;
  readonly spans?: ReadonlyArray<SpanInput>;
  readonly tags?: Readonly<Record<string, string>>;
}): void {
  if (!dsn) return;
  if (tracesSampleRate <= 0 || Math.random() >= tracesSampleRate) return;
  const traceId = randomHex(16);
  const rootSpanId = randomHex(8);
  const eventId = randomHex(16);
  const spans = (input.spans ?? []).map((s) => ({
    span_id: randomHex(8),
    parent_span_id: rootSpanId,
    trace_id: traceId,
    op: s.op,
    ...(s.description ? { description: s.description } : {}),
    start_timestamp: s.startMs / 1000,
    timestamp: s.endMs / 1000,
  }));
  const envelopeHeader = JSON.stringify({ event_id: eventId, sent_at: new Date().toISOString() });
  const itemHeader = JSON.stringify({ type: "transaction" });
  const payload = JSON.stringify({
    event_id: eventId,
    type: "transaction",
    transaction: input.name,
    start_timestamp: input.startMs / 1000,
    timestamp: input.endMs / 1000,
    platform: "javascript",
    environment,
    ...(release ? { release } : {}),
    tags: { surface: SURFACE, ...input.tags },
    ...(currentUser ? { user: currentUser } : {}),
    contexts: { trace: { trace_id: traceId, span_id: rootSpanId, op: input.op } },
    spans,
  });
  const url = `https://${dsn.host}/api/${dsn.projectId}/envelope/?sentry_key=${dsn.publicKey}&sentry_version=7`;
  try {
    void fetch(url, {
      method: "POST",
      keepalive: true,
      headers: { "content-type": "application/x-sentry-envelope" },
      body: `${envelopeHeader}\n${itemHeader}\n${payload}\n`,
    }).catch(() => {});
  } catch {
    // ignore — telemetry must never throw into the app
  }
}
